import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { sortRows } from 'Utils/SortableTh';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { uiLabel } from 'Utils/Labeling';
import { STATUS, INK, FONT_MONO } from 'Utils/styleTokens';
import {
  groupRowsByArea, areaNoun, areaKindForSource, sizeAreaTiles, shortAreaLabel, areaQualifierLabel,
  AREA_KIND,
} from '../areaUtils';
import { SEVERITY_STYLE, LEVEL_PILL, countNoun } from '../edlUtils';
import SortHeader from './SortHeader';

const KIND_HEADING = {
  [AREA_KIND.TABLE]: 'Table',
  [AREA_KIND.FOLDER]: 'Folder',
  [AREA_KIND.COLLECTION]: 'Collection',
};

const TOP_ENTITIES = 3;
const UNRATED = 'UNRATED';
const LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

// Areas reuse the home grid's severity palette so the drill-in reads as the same map one level
// down. An area whose entities are in no severity tier gets the neutral "never scanned" swatch.
const tileStyle = (area) => SEVERITY_STYLE[area.worstLevel] || SEVERITY_STYLE.NEVER_SCANNED;
const levelOf = (area) => area.worstLevel || UNRATED;

/**
 * "Where inside this data source" — the level between the Enterprise landscape's per-source tile
 * grid and the per-(location, entity) ledger. Same map idiom as /enterprise, one level down:
 * one tile per table / folder / collection, sized by finding volume and coloured by the worst
 * severity tier present, so the areas that matter are visible without reading a column of numbers.
 *
 * Clicking a tile ticks that area's open findings and opens the ledger scrolled to its group, so
 * the CSV handoff is one click away. A table view is available for sorting and exact comparison.
 */
const AreaOverview = ({
  url, source, rows, diffError, diffUnsupported, loadingDiff, progress, toId, levelByEntity,
  setRowsSelected, clearSelection,
}) => {
  const history = useHistory();
  const [query, setQuery] = useState('');
  const [view, setView] = useState('map');
  // Legend swatches double as filters, exactly as the home grid's severity legend does.
  const [levelFilter, setLevelFilter] = useState([]);
  const [sort, setSort] = useState(null);

  const areas = useMemo(
    () => groupRowsByArea(rows, source, levelByEntity),
    [rows, source, levelByEntity],
  );

  const kind = areaKindForSource(source);
  const noun = areaNoun(source);
  const nounSingular = areaNoun(source, { plural: false });
  // What the number in each tile counts: columns on a database, findings in a file corpus.
  // Same unit as the page header's KPIs, so the tiles add up to them.
  const countNounPlural = countNoun(source);
  const countNounSingular = countNoun(source, { plural: false });

  const counts = useMemo(() => {
    const c = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, [UNRATED]: 0 };
    areas.forEach((a) => { c[levelOf(a)] += 1; });
    return c;
  }, [areas]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return areas.filter((a) => (!q || a.label.toLowerCase().includes(q))
      && (levelFilter.length === 0 || levelFilter.includes(levelOf(a))));
  }, [areas, query, levelFilter]);

  const sortAccessors = useMemo(() => ({
    area: (a) => a.label,
    total: (a) => a.total,
    new: (a) => a.new,
    persisting: (a) => a.persisting,
    resolved: (a) => a.resolved,
  }), []);

  const visible = useMemo(() => sortRows(filtered, sort, sortAccessors), [filtered, sort, sortAccessors]);
  const tiles = useMemo(() => sizeAreaTiles(visible), [visible]);

  const toggleLevel = (lvl) => setLevelFilter((prev) => (
    prev.includes(lvl) ? prev.filter((l) => l !== lvl) : [...prev, lvl]
  ));

  const openArea = (area) => {
    // Start clean so "Selected for handoff" reflects exactly the area picked; other areas can be
    // added from the ledger's own per-heading controls.
    clearSelection();
    setRowsSelected(area.rows, true);
    history.push(`${url}/findings`, { area: area.key });
  };

  if (toId == null) {
    return (
      <div className='edl-card'>
        <div className='sg-small' data-testid='edl-no-pair'>
          A completed discovery run is needed to compare. Run discovery on this source to see its findings
          here; a second run then shows what was resolved, what persists and what is new.
        </div>
      </div>
    );
  }

  if (diffError) {
    return (
      <div className='edl-card'>
        <div
          className='sg-small'
          style={{ color: diffUnsupported ? INK[500] : STATUS.danger.text }}
          data-testid='edl-diff-error'
          data-unsupported={diffUnsupported ? 'true' : 'false'}
        >
          {diffError}
        </div>
        {diffUnsupported && (
          <div className='sg-small' style={{ color: INK[400], marginTop: 8 }}>
            The severity and finding totals on the landscape come from the platform's own roll-up, so
            they are unaffected — only the run-to-run comparison needs a discover-worker run.
          </div>
        )}
      </div>
    );
  }

  const legend = [...LEVELS, UNRATED].filter((l) => counts[l] > 0);

  return (
    <div className='edl-card' data-testid='edl-area-overview'>
      <div className='edl-area-bar'>
        <div className='edl-legend' role='group' aria-label={`Filter ${noun} by severity level`}>
          {legend.map((lvl) => {
            const on = levelFilter.length === 0 || levelFilter.includes(lvl);
            const st = SEVERITY_STYLE[lvl] || SEVERITY_STYLE.NEVER_SCANNED;
            return (
              <button
                key={lvl}
                type='button'
                className={`edl-legend__toggle${on ? '' : ' edl-legend__toggle--off'}`}
                onClick={() => toggleLevel(lvl)}
                data-testid={`edl-area-level-${lvl}`}
                title={`Show only ${nounSingular}s at this level`}
              >
                <span className='edl-legend__swatch' style={{ background: st.bg, border: st.dashed ? '1px dashed var(--ink-300)' : undefined }} />
                {(LEVEL_PILL[lvl] || {}).label || 'Unrated'} {counts[lvl]}
              </button>
            );
          })}
          {levelFilter.length > 0 && (
            <button type='button' className='edl-legend__toggle' onClick={() => setLevelFilter([])} data-testid='edl-area-level-clear'>
              clear
            </button>
          )}
        </div>
        <div className='edl-area-bar__right'>
          <input
            className='edl-search'
            placeholder={`Search ${noun}…`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid='edl-area-search'
          />
          <span className='sg-small' data-testid='edl-area-count'>
            {visible.length.toLocaleString()} {visible.length === 1 ? nounSingular : noun} with findings
          </span>
          <div className='edl-viewtoggle' role='group' aria-label='View'>
            <button type='button' className={`edl-chip${view === 'map' ? ' edl-chip--on' : ''}`}
              onClick={() => setView('map')} data-testid='edl-view-map'>Map</button>
            <button type='button' className={`edl-chip${view === 'table' ? ' edl-chip--on' : ''}`}
              onClick={() => setView('table')} data-testid='edl-view-table'>Table</button>
          </div>
        </div>
      </div>

      {loadingDiff ? (
        <div className='sg-small' style={{ padding: '18px 2px' }} data-testid='edl-area-loading'>
          {progress && progress.total
            ? `Loading changes… ${progress.loaded.toLocaleString()} of ${progress.total.toLocaleString()}`
            : 'Loading changes…'}
        </div>
      ) : visible.length === 0 ? (
        <div className='sg-small' style={{ padding: '18px 2px' }} data-testid='edl-area-empty'>
          {rows.length === 0
            ? `No findings in this comparison — nothing to attribute to a ${nounSingular}.`
            : `No ${noun} match the current filters.`}
        </div>
      ) : view === 'map' ? (
        <div className='edl-grid' data-testid='edl-area-grid'>
          {tiles.map(({ area, span }) => {
            const st = tileStyle(area);
            const classes = ['edl-tile', `edl-tile--${span}`, 'edl-tile--clickable', 'edl-area-tile'];
            if (st.dashed) classes.push('edl-tile--light');
            return (
              <button
                type='button'
                key={area.key || '(root)'}
                className={classes.join(' ')}
                style={{ backgroundColor: st.bg, color: st.fg }}
                onClick={() => openArea(area)}
                data-testid='edl-area-item'
                data-area={area.key}
                data-span={span}
                title={`${area.label} · ${area.total.toLocaleString()} ${area.total === 1 ? countNounSingular : countNounPlural} · ${area.openCount.toLocaleString()} open`}
              >
                <div className='edl-tile__head'>
                  <div style={{ minWidth: 0 }}>
                    <div className='edl-tile__name'>{shortAreaLabel(area)}</div>
                    {/* Schema (or NoSQL database, or parent folder) on a second line rather than
                        as a "schema.table_name" prefix — the table name is what is being read. */}
                    {areaQualifierLabel(area) && (
                      <div className='edl-tile__qual' data-testid='edl-area-qualifier'>{areaQualifierLabel(area)}</div>
                    )}
                  </div>
                </div>
                <div className='edl-tile__sub'>
                  {area.new > 0 ? `↑ ${area.new.toLocaleString()} new` : `${area.openCount.toLocaleString()} open`}
                </div>
                <div className='edl-tile__big'>{area.total.toLocaleString()}</div>
                {span !== '1x1' && (
                  <div className='edl-area-tile__ents'>
                    {area.topEntities.slice(0, span === '3x2' ? 4 : 2).map((e) => (
                      <span className='edl-area-tile__ent' key={e.entity} title={uiLabel(e.entity)}>{e.entity}</span>
                    ))}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div className='edl-area-table'>
          <div className='edl-area-row--head'>
            <SortHeader label={KIND_HEADING[kind] || 'Area'} sortKey='area' sort={sort} onSort={setSort} />
            <div role='columnheader'>Level</div>
            <SortHeader
              label={countNounPlural.charAt(0).toUpperCase() + countNounPlural.slice(1)}
              sortKey='total' sort={sort} onSort={setSort}
              title={`Sort by ${countNounPlural} with a finding in this comparison`}
            />
            <SortHeader label='New' sortKey='new' sort={sort} onSort={setSort} />
            <SortHeader label='Persisting' sortKey='persisting' sort={sort} onSort={setSort} />
            <SortHeader label='Resolved' sortKey='resolved' sort={sort} onSort={setSort} />
            <div role='columnheader'>Top entities</div>
            <div role='columnheader' aria-label='Open' />
          </div>
          {visible.map((area) => {
            const lvl = LEVEL_PILL[area.worstLevel];
            return (
              <button
                type='button'
                className='edl-area-row'
                key={area.key || '(root)'}
                data-testid='edl-area-item'
                data-area={area.key}
                onClick={() => openArea(area)}
                title={`Open the ${area.openCount.toLocaleString()} open ${area.openCount === 1 ? countNounSingular : countNounPlural} in ${area.label}`}
              >
                <div style={{ minWidth: 0 }}>
                  <DiscoveryTooltip placement='top' text={<span style={{ fontFamily: FONT_MONO, wordBreak: 'break-all' }}>{area.label}</span>}>
                    <span className='edl-row__loc'><bdi>{area.name || area.label}</bdi></span>
                  </DiscoveryTooltip>
                  {areaQualifierLabel(area) && (
                    <div className='edl-area-row__qual' data-testid='edl-area-qualifier'>
                      <bdi>{areaQualifierLabel(area)}</bdi>
                    </div>
                  )}
                </div>
                {lvl ? (
                  <span className='sg-severity' style={{ background: lvl.bg, color: lvl.fg, justifySelf: 'start' }} data-testid='edl-area-level'>{lvl.label}</span>
                ) : (
                  <span className='edl-row__spans' data-testid='edl-area-level' title='No entity here is assigned to a landscape severity tier'>—</span>
                )}
                <span className='edl-row__spans' data-testid='edl-area-total'>{area.total.toLocaleString()}</span>
                <span className='edl-row__spans' style={{ color: area.new > 0 ? STATUS.danger.text : undefined }} data-testid='edl-area-new'>
                  {area.new.toLocaleString()}
                </span>
                <span className='edl-row__spans' style={{ color: area.persisting > 0 ? STATUS.warning.text : undefined }}>
                  {area.persisting.toLocaleString()}
                </span>
                <span className='edl-row__spans' style={{ color: area.resolved > 0 ? STATUS.success.text : undefined }}>
                  {area.resolved.toLocaleString()}
                </span>
                <span className='edl-area-row__ents'>
                  <span className='edl-area-row__chips'>
                    {area.topEntities.slice(0, TOP_ENTITIES).map((e) => (
                      <span className='edl-row__ent' key={e.entity} title={uiLabel(e.entity)}>{e.entity}</span>
                    ))}
                  </span>
                  {area.topEntities.length > TOP_ENTITIES && (
                    <span className='edl-area-row__more' style={{ color: INK[400] }}
                      title={area.topEntities.slice(TOP_ENTITIES).map((e) => e.entity).join(', ')}>
                      +{area.topEntities.length - TOP_ENTITIES}
                    </span>
                  )}
                </span>
                <span className='edl-area-row__go' aria-hidden='true'>→</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AreaOverview;
